import { convertBytes } from '@waitroom/utils';
import { TFunction } from 'i18next';
import { z } from 'zod';
import { commonConfig } from '../config';

export interface ValidateFileOptions {
  t: TFunction;
  field: string;
  size?: number;
  types?: string[];
  required?: boolean;
  existingFile?: string;
}

/**
 * Validate file
 */
export const validateFile = ({
  t,
  size,
  types,
  field,
  required,
  existingFile,
}: ValidateFileOptions): z.Schema => {
  const validation = z.instanceof(File).superRefine((f, ctx) => {
    if (required && !f) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.required', {
          field,
        }),
      });
    }

    if (f && size && f.size > size) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.tooLarge', {
          field,
          max: `${convertBytes(size, 'B', 'MB', 2)}MB`,
        }),
      });
    }

    if (f && types && !types.includes(f.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation.unsupportedFileType'),
      });
    }
  });

  return existingFile ? z.optional(validation) : validation;
};

/**
 * Validate image
 */
export const validateImage = ({
  size = commonConfig.maxImageSize,
  types = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png', 'image/svg', 'image/svg+xml'],
  ...rest
}: ValidateFileOptions): z.Schema =>
  validateFile({
    size,
    types,
    ...rest,
  });

/**
 * Validate min max
 */
export const validateMinMax = (
  t: TFunction,
  { min, max, field }: { min: number; max: number; field: string },
): z.ZodString =>
  z
    .string()
    .min(min, t('validation.tooShort', { field, min }))
    .max(max, t('validation.tooLong', { field, max }));

/**
 * Validate url slug
 */
export const validateSlug = (t: TFunction, field: string): z.ZodString =>
  validateMinMax(t, { min: 1, max: 255, field }).regex(
    /^[a-zA-Z0-9\-_]+$/,
    t('validation.invalid', { field }),
  );

/**
 * Validate domain
 */
export const validateDomain = (t: TFunction, field: string): z.ZodString =>
  z.string().regex(/^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/, t('validation.invalid', { field }));

/**
 * Validate rtmp url
 */
export const validateRtmpUrl = (t: TFunction, field: string): z.ZodString =>
  z.string().regex(/^((rtmps?):\/\/.*\/.*)?$/, t('validation.invalid', { field }));

/**
 * Validate first name
 */
export const validateFirstName = (t: TFunction, field: string): z.ZodString =>
  validateMinMax(t, { min: 1, max: 100, field }).trim();

/**
 * Validate last name
 */
export const validateLastName = validateFirstName;

export const stripEmptyString = (value: string | undefined) => (value !== '' ? value : undefined);

export const excludedEmailDomains = ['gmail.com', 'yahoo.com'];
export const filterEmailDomains = (value: string): boolean => {
  const domain = value.split('@')[1];
  return !excludedEmailDomains.includes(domain);
};
